import React, { useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { RefreshCw } from 'lucide-react';
import { PaymentStatusChip } from './PaymentStatusChip';
import { usePaymentDetail, useReconcilePayment } from '../hooks/usePayments';
import { REASON_LABEL, errorText, formatDateTime, formatVnd, reviewReason } from '../format';

interface Props {
  publicId: string | null;
  onClose: () => void;
}

const Field: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div>
    <div className="text-xs text-gray-500 dark:text-slate-400">{label}</div>
    <div className="break-all text-sm">{value ?? '-'}</div>
  </div>
);

export const PaymentDetailDialog: React.FC<Props> = ({ publicId, onClose }) => {
  const { data, isLoading, error } = usePaymentDetail(publicId);
  const reconcile = useReconcilePayment();
  const [confirming, setConfirming] = useState(false);
  const [message, setMessage] = useState<{
    severity: 'success' | 'info' | 'error';
    text: string;
  } | null>(null);

  const close = () => {
    setConfirming(false);
    setMessage(null);
    onClose();
  };

  const run = () => {
    if (!publicId) return;
    reconcile.mutate(publicId, {
      onSuccess: ({ outcome }) => {
        setConfirming(false);
        setMessage(
          outcome.settled
            ? { severity: 'success', text: 'Payment confirmed with the gateway and applied.' }
            : {
                severity: 'info',
                text: REASON_LABEL[outcome.reason ?? ''] ?? `Status: ${outcome.status}`,
              }
        );
      },
      onError: (err) => {
        setConfirming(false);
        setMessage({ severity: 'error', text: errorText(err) });
      },
    });
  };

  const canReconcile =
    data && ['PENDING', 'NEEDS_REVIEW', 'EXPIRED', 'CANCELED'].includes(data.status);
  const review = data ? reviewReason(data.metadata) : null;

  return (
    <Dialog
      open={publicId !== null}
      onClose={reconcile.isPending ? undefined : close}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Payment details</DialogTitle>
      <DialogContent>
        {isLoading && (
          <div className="flex justify-center p-6">
            <CircularProgress />
          </div>
        )}
        {error && <Alert severity="error">{errorText(error)}</Alert>}
        {data && (
          <div className="space-y-4">
            {message && <Alert severity={message.severity}>{message.text}</Alert>}
            {data.status === 'NEEDS_REVIEW' && (
              <Alert severity="warning">
                {review ?? 'This payment needs manual review.'} Reconcile re-checks the gateway;
                refunds and manual grants are handled outside this screen.
              </Alert>
            )}
            <div className="flex items-center gap-3">
              <PaymentStatusChip status={data.status} />
              <span className="text-sm text-gray-600 dark:text-slate-400">{data.kind}</span>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
              <Field label="Customer" value={data.user.email} />
              <Field label="Item" value={data.item?.name} />
              <Field label="Invoice" value={data.invoiceNumber} />
              <Field label="List price" value={formatVnd(data.listAmountMinor)} />
              <Field
                label="Discount"
                value={
                  data.discountMinor > 0
                    ? `-${formatVnd(data.discountMinor)}${data.coupon ? ` (${data.coupon.code})` : ''}`
                    : '-'
                }
              />
              <Field label="Amount due" value={formatVnd(data.amountMinor)} />
              <Field label="Amount paid" value={formatVnd(data.paidAmountMinor)} />
              <Field label="Created" value={formatDateTime(data.createdAt)} />
              <Field label="Expires" value={formatDateTime(data.expiresAt)} />
              <Field label="Paid at" value={formatDateTime(data.paidAt)} />
              <Field label="Gateway order" value={data.sepayOrderId} />
              <Field label="Gateway transaction" value={data.externalRef} />
            </div>

            <div>
              <div className="mb-1 text-sm font-medium">
                Gateway notifications ({data.ipnEvents.length})
              </div>
              {data.ipnEvents.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-slate-400">None received.</p>
              ) : (
                <div className="space-y-2">
                  {data.ipnEvents.map((event) => (
                    <details
                      key={event.publicId}
                      className="rounded border border-gray-200 p-2 text-sm dark:border-slate-700"
                    >
                      <summary className="cursor-pointer">
                        {formatDateTime(event.createdAt)} · {event.notificationType} ·{' '}
                        {event.status}
                        {event.error ? ` · ${event.error}` : ''}
                      </summary>
                      <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap text-xs">
                        {JSON.stringify(event.rawPayload, null, 2)}
                      </pre>
                    </details>
                  ))}
                </div>
              )}
            </div>

            {data.metadata && typeof data.metadata === 'object' && (
              <details className="text-sm">
                <summary className="cursor-pointer">Metadata</summary>
                <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap text-xs">
                  {JSON.stringify(data.metadata, null, 2)}
                </pre>
              </details>
            )}

            {confirming && (
              <Alert
                severity="warning"
                action={
                  <Button color="inherit" size="small" onClick={run} disabled={reconcile.isPending}>
                    Confirm
                  </Button>
                }
              >
                Ask the gateway for this order&apos;s status and apply the result? A captured
                payment will be granted to the customer.
              </Alert>
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {canReconcile && (
          <Button
            startIcon={<RefreshCw size={16} />}
            onClick={() => setConfirming(true)}
            disabled={reconcile.isPending || confirming}
          >
            Reconcile
          </Button>
        )}
        <Button onClick={close} color="inherit" disabled={reconcile.isPending}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
